/**
 * AppSidebar — Primary navigation using navConfig + Disclosure pattern.
 *
 * Features:
 * - Shell-aware (Operations vs Admin Console)
 * - Role + permission-key + capability filtering via useNavigation
 * - W3C APG Disclosure Navigation for groups
 * - Starred items with star toggle
 * - Skeleton loading until effective-access + prefs ready
 * - Shell switcher link
 * - Instrumentation via trackNavClick
 *
 * @see docs/ux/navigation-ia.md
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/examples/disclosure-navigation/
 */

import { useState, useRef, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTranslationWithCMS } from '@/i18n/useTranslationWithCMS';
import { safeShellT, type SafeShellTFunction } from '@/i18n/safeShellT';
import {
  ChevronRight,
  ChevronUp,
  ExternalLink,
  Star,
  Home,
  Settings,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/hooks/useQueryHooks';
import { useNavigation } from '@/hooks/useNavigation';
import { trackNavClick } from '@/hooks/useNavInstrumentation';
import { useDynamicFavicon } from '@/hooks/useDynamicFavicon';
import { toRelativeUploadUrl } from '@/lib/utils';
import type { NavShell, NavItemConfig, NavGroupConfig } from '@/config/nav';
import { resolveNavLabel, resolveNavGroupLabel } from '@/config/nav';
import { useTerminology } from '@/hooks/useTerminology';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { UserAvatar } from '@/components/common';

// ── Disclosure Group (W3C APG pattern) ──────────────────────────────────────

function NavDisclosureGroup({
  group,
  groupLabel,
  defaultOpen = true,
  children,
}: {
  group: NavGroupConfig;
  /** Pre-resolved group label (TRP-414). Pass `resolveNavGroupLabel(group, t)`. */
  groupLabel: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const contentId = `nav-group-${group.id}`;

  return (
    <SidebarGroup>
      <SidebarGroupLabel asChild>
        <button
          ref={buttonRef}
          type="button"
          aria-expanded={isOpen}
          aria-controls={contentId}
          onClick={() => setIsOpen((prev) => !prev)}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && isOpen) {
              e.stopPropagation();
              setIsOpen(false);
              buttonRef.current?.focus();
            }
          }}
          className="w-full"
        >
          <span className="flex-1 text-left">{groupLabel}</span>
          <ChevronRight
            className={cn(
              'ml-auto h-3 w-3 shrink-0 transition-transform duration-200',
              isOpen && 'rotate-90',
            )}
          />
        </button>
      </SidebarGroupLabel>
      {isOpen && (
        <SidebarGroupContent id={contentId} role="group">
          <SidebarMenu>{children}</SidebarMenu>
        </SidebarGroupContent>
      )}
    </SidebarGroup>
  );
}

// ── Nav Item ────────────────────────────────────────────────────────────────

function NavLink({
  item,
  label,
  isActive,
  isStarred,
  onStar,
  onNavigate,
  t,
}: {
  item: NavItemConfig;
  label: string;
  isActive: boolean;
  isStarred: boolean;
  onStar: () => void;
  onNavigate: () => void;
  /** Translation function from useTranslationWithCMS — passed through for aria/title. */
  t: SafeShellTFunction;
}) {
  // TRP-414: aria-label + title resolved via safeShellT with interpolation
  const ariaLabel = isStarred
    ? safeShellT(t, 'shell.starred.remove.aria', {
        fallback: `Unstar ${label}`,
        values: { label },
      })
    : safeShellT(t, 'shell.starred.add.aria', {
        fallback: `Star ${label}`,
        values: { label },
      });
  const titleAttr = isStarred
    ? safeShellT(t, 'shell.starred.remove.title', { fallback: 'Remove from starred' })
    : safeShellT(t, 'shell.starred.add.title', { fallback: 'Add to starred' });
  // External links open in a new tab
  if (item.isExternal && item.externalUrl) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton asChild isActive={false} tooltip={label}>
          <a
            href={item.externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onNavigate}
          >
            <item.icon className="h-4 w-4" />
            <span>{label}</span>
            <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
          </a>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive} tooltip={label}>
        <Link to={item.path} onClick={onNavigate}>
          <item.icon className="h-4 w-4" />
          <span>{label}</span>
        </Link>
      </SidebarMenuButton>
      <SidebarMenuAction
        showOnHover
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onStar();
        }}
        aria-label={ariaLabel}
        title={titleAttr}
      >
        <Star
          className={cn(
            'h-3.5 w-3.5',
            isStarred && 'fill-current text-amber-500',
          )}
        />
      </SidebarMenuAction>
    </SidebarMenuItem>
  );
}

// ── Sidebar Skeleton ────────────────────────────────────────────────────────

function SidebarSkeleton({
  shell,
  side,
  appName,
  adminBadge,
}: {
  shell: NavShell;
  side: 'left' | 'right';
  /** Pre-resolved app brand name (TRP-414). Default brand: "AMuster". */
  appName: string;
  /** Pre-resolved admin-console badge label (TRP-414). */
  adminBadge: string;
}) {
  return (
    <Sidebar side={side} collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" className="pointer-events-none">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-500 text-white font-bold text-sm">
                A
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="text-lg font-bold text-gray-900 truncate">{appName}</span>
                {shell === 'admin' && (
                  <span className="text-xs text-muted-foreground">{adminBadge}</span>
                )}
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {Array.from({ length: 4 }).map((_, i) => (
          <SidebarGroup key={i}>
            <SidebarGroupLabel>
              <Skeleton className="h-3.5 w-16" />
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {Array.from({ length: 3 }).map((_, j) => (
                  <SidebarMenuItem key={j}>
                    <SidebarMenuSkeleton showIcon />
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuSkeleton showIcon />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────

export function AppSidebar({ shell }: { shell: NavShell }) {
  const { user, tenant, logout } = useAuth();
  const terminology = useTerminology();
  // ── RTL sidebar docking (LANGUAGE-SWITCHER-USER-SHELL-RTL-SIDEBAR-A1) ─────
  // Drive the shadcn <Sidebar> `side` prop from i18next so the tenant/user
  // shell docks the sidebar on the right under RTL (Arabic) and on the left
  // under LTR (English). i18n.dir() returns 'ltr' | 'rtl' based on the
  // currently-active locale, and useTranslation re-renders on the existing
  // i18next `languageChanged` event — the same event that already drives
  // applyLocaleDirection() / <html dir> in apps/web/src/i18n/config.ts.
  // Persistence and reload follow the existing amuster_locale path; this
  // hook adds no new persistence and no new direction source of truth.
  // Falls back to 'left' for any non-'rtl' return value (including unset).
  const { i18n } = useTranslation();
  const side: 'left' | 'right' = i18n.dir() === 'rtl' ? 'right' : 'left';
  // TRP-414: tenant-user shell i18n. useTranslationWithCMS is the canonical
  // hook (already used by LanguageSelector + RegulatoryRegisterPage); shell
  // labels go through safeShellT to guard against raw-key leaks under AR.
  const { t: rawT } = useTranslationWithCMS();
  // Pre-resolve shell-level labels at component scope so they update on
  // locale change (useTranslation re-renders on languageChanged).
  const appName = safeShellT(rawT, 'shell.app.name', { fallback: 'AMuster' });
  const adminBadge = safeShellT(rawT, 'shell.adminConsoleBadge', { fallback: 'Admin Console' });
  const logoAlt = safeShellT(rawT, 'shell.app.logoAlt', { fallback: 'Logo' });
  const starredGroupLabel = safeShellT(rawT, 'shell.starred.group.label', { fallback: 'Starred' });
  const switchToAdminLabel = safeShellT(rawT, 'shell.shellSwitch.toAdmin.label', { fallback: 'Admin Console' });
  const switchToAdminTooltip = safeShellT(rawT, 'shell.shellSwitch.toAdmin.tooltip', { fallback: 'Admin Console' });
  const switchToOpsLabel = safeShellT(rawT, 'shell.shellSwitch.toOps.label', { fallback: 'Back to Operations' });
  const switchToOpsTooltip = safeShellT(rawT, 'shell.shellSwitch.toOps.tooltip', { fallback: 'Back to Operations' });
  const userFallbackName = safeShellT(rawT, 'shell.user.fallback.name', { fallback: 'User' });
  const userFallbackRole = safeShellT(rawT, 'shell.user.fallback.role', { fallback: 'User' });
  const menuProfile = safeShellT(rawT, 'shell.user.menu.profile', { fallback: 'Profile' });
  const menuPreferences = safeShellT(rawT, 'shell.user.menu.preferences', { fallback: 'Preferences' });
  const menuLogout = safeShellT(rawT, 'shell.user.menu.logout', { fallback: 'Log out' });
  const { data: tenantData } = useTenant();
  const tenantBranding = tenantData?.data?.branding;
  const tenantFavicon = useMemo(() => {
    const fav = (tenantBranding as Record<string, unknown> | undefined)?.favicon_url as string | null | undefined;
    if (fav) return toRelativeUploadUrl(fav) || fav;
    // Derive 64px variant from main logo URL
    const logo = (tenantBranding as Record<string, unknown> | undefined)?.logo_url as string | null | undefined;
    if (logo) {
      const relativeLogo = toRelativeUploadUrl(logo) || logo;
      return relativeLogo.replace(/-512\.webp$/, '-64.webp');
    }
    return null;
  }, [tenantBranding]);
  useDynamicFavicon(tenantFavicon);
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';
  const location = useLocation();
  const [logoError, setLogoError] = useState(false);

  const {
    groups,
    groupedItems,
    starredItems,
    isLoading,
    toggleStarred,
    trackVisit,
  } = useNavigation(shell);

  // Starred item IDs for quick lookup
  const starredSet = new Set(starredItems.map((i) => i.id));

  // Active item detection (longest prefix match within visible items)
  const getIsActive = (item: NavItemConfig): boolean => {
    const pathMatches =
      location.pathname === item.path ||
      location.pathname.startsWith(item.path + '/');
    if (!pathMatches) return false;
    // Ensure no sibling with longer matching path
    const siblings = groupedItems.get(item.group) || [];
    return !siblings.some(
      (other) =>
        other.path !== item.path &&
        other.path.length > item.path.length &&
        (location.pathname === other.path ||
          location.pathname.startsWith(other.path + '/')),
    );
  };

  const handleNavigate = (item: NavItemConfig) => {
    trackNavClick(item.id, item.route_id, location.pathname);
    trackVisit(item.id);
  };

  // Show skeleton until data is ready (zero-flicker boot)
  if (isLoading) {
    return <SidebarSkeleton shell={shell} side={side} appName={appName} adminBadge={adminBadge} />;
  }

  return (
    <Sidebar side={side} collapsible="icon">
      {/* Logo Header + Shell indicator */}
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link
                to={shell === 'admin' ? '/admin/campuses' : '/dashboard'}
                className="flex items-center gap-2"
              >
                {tenantBranding?.logo_url && !logoError ? (
                  <img
                    src={toRelativeUploadUrl(tenantBranding.logo_url) || tenantBranding.logo_url}
                    alt={tenantBranding.app_name || logoAlt}
                    className="h-8 w-8 shrink-0 rounded-lg object-contain"
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-500 text-white font-bold text-sm">
                    {(tenantBranding?.app_name || tenant?.name || appName).charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="text-lg font-bold text-gray-900 truncate">
                    {tenantBranding?.app_name || tenant?.name || appName}
                  </span>
                  {shell === 'admin' && (
                    <span className="text-xs text-muted-foreground">
                      {adminBadge}
                    </span>
                  )}
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      {/* Navigation Content */}
      <SidebarContent>
        {/* Shell switcher */}
        <SidebarGroup>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip={
                  shell === 'operations'
                    ? switchToAdminTooltip
                    : switchToOpsTooltip
                }
              >
                <Link
                  to={
                    shell === 'operations' ? '/admin/campuses' : '/dashboard'
                  }
                >
                  {shell === 'operations' ? (
                    <Settings className="h-4 w-4" />
                  ) : (
                    <Home className="h-4 w-4" />
                  )}
                  <span>
                    {shell === 'operations'
                      ? switchToAdminLabel
                      : switchToOpsLabel}
                  </span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>

        {/* Starred items */}
        {starredItems.length > 0 && (
          <NavDisclosureGroup
            group={{
              id: 'starred',
              label: 'Starred',
              labelKey: 'shell.starred.group.label',
              shell,
              order: -1,
            }}
            groupLabel={starredGroupLabel}
          >
            {starredItems.map((item) => (
              <NavLink
                key={item.id}
                item={item}
                label={resolveNavLabel(item, terminology, rawT)}
                isActive={getIsActive(item)}
                isStarred
                onStar={() => toggleStarred(item.id)}
                onNavigate={() => handleNavigate(item)}
                t={rawT}
              />
            ))}
          </NavDisclosureGroup>
        )}

        {/* Grouped nav items */}
        {groups.map((group) => {
          const items = groupedItems.get(group.id);
          if (!items?.length) return null;

          return (
            <NavDisclosureGroup
              key={group.id}
              group={group}
              groupLabel={resolveNavGroupLabel(group, rawT)}
            >
              {items.map((item) => (
                <NavLink
                  key={item.id}
                  item={item}
                  label={resolveNavLabel(item, terminology, rawT)}
                  isActive={getIsActive(item)}
                  isStarred={starredSet.has(item.id)}
                  onStar={() => toggleStarred(item.id)}
                  onNavigate={() => handleNavigate(item)}
                  t={rawT}
                />
              ))}
            </NavDisclosureGroup>
          );
        })}
      </SidebarContent>

      {/* User Footer */}
      <SidebarFooter>
        {/* Tenant indicator (expanded only) */}
        {!isCollapsed && tenant && (
          <p className="px-2 text-xs font-medium text-muted-foreground uppercase tracking-wider truncate">
            {tenant.name}
          </p>
        )}

        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <UserAvatar
                    photoUrl={user?.photo_url}
                    name={user?.name_en}
                    size="sm"
                  />
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {user?.name_en || userFallbackName}
                    </span>
                    <span className="truncate text-xs text-muted-foreground capitalize">
                      {user?.role?.replace(/_/g, ' ') || userFallbackRole}
                    </span>
                  </div>
                  <ChevronUp className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="top"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuItem asChild>
                  <Link to="/user/profile">{menuProfile}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/user/preferences">{menuPreferences}</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => logout()}
                  className="text-destructive focus:text-destructive"
                >
                  {menuLogout}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      {/* Rail for resize/toggle */}
      <SidebarRail />
    </Sidebar>
  );
}
